import type { DebouncedFunc, DebounceSettings } from 'lodash';
import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';

/**
 * Wrapper around `lodash/debounce` that returns a debounced version of the given
 * function. The debounced function is stable across renders.
 *
 * @param func The function to debounce.
 * @param wait The number of milliseconds to wait before invoking the function.
 * @param options See `lodash/debounce` options.
 */
export function useDebounce<T extends (...args: any[]) => any>(
  func: T,
  wait?: number,
  options?: DebounceSettings,
): DebouncedFunc<T> {
  const debouncedFunc = useRef<DebouncedFunc<T>>(debounce(func, wait, options));

  useEffect(
    () => () => {
      debouncedFunc.current.flush();
    },
    [debouncedFunc],
  );

  return debouncedFunc.current;
}

/**
 * Debounces a changing value across re-renders.
 * @param options See `lodash/debounce` options.
 */
export function useDebouncedValue<T>(
  value: T,
  wait?: number,
  options?: DebounceSettings,
) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const debouncedSetter = useDebounce(setDebouncedValue, wait, options);
  useEffect(() => debouncedSetter(value), [value, debouncedSetter]);

  return debouncedValue;
}
